import React from "react";
import { MapPin, Plus, Home, Building, Edit2, Star, Trash2, Navigation } from "lucide-react";

// Build a Google Maps directions/search URL from a saved pin. Uses the
// universal Maps URL scheme so it opens the native app on mobile and the web
// map on desktop — no JS API or billing required (avoids BillingNotEnabledMapError).
const mapsUrlFor = (lat, lng) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lat)}%2C${encodeURIComponent(lng)}`;

const hasPin = (addr) =>
  addr?.location_lat !== null && addr?.location_lat !== undefined && addr?.location_lat !== "" &&
  addr?.location_lng !== null && addr?.location_lng !== undefined && addr?.location_lng !== "";

export const AddressManager = ({
  addresses, editingAddr, setEditingAddr, addrForm, setAddrForm,
  addrLoading, handleSaveAddress, handleDeleteAddress, handleSetDefault,
}) => {
  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-[#f0e6d9] p-5" data-testid="customer-addresses">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[#1E1B4B] uppercase tracking-wider flex items-center gap-2"><MapPin size={16} /> My Addresses</h3>
        {editingAddr === null && (
          <button
            onClick={() => { setEditingAddr('new'); setAddrForm({ label: 'Home', address: '', pincode: '' }); }}
            className="flex items-center gap-1 text-xs text-[#E83E8C] font-medium hover:underline"
            data-testid="add-address-btn"
          >
            <Plus size={13} /> Add New
          </button>
        )}
      </div>

      {addresses.length > 0 ? (
        <div className="space-y-2.5">
          {addresses.map(addr => (
            <div key={addr.address_id} className={`p-3 rounded-xl border transition-all ${addr.is_default ? 'border-[#E83E8C]/30 bg-[#E83E8C]/5' : 'border-[#f0e6d9] bg-[#FFFAF5]'}`} data-testid={`portal-address-${addr.address_id}`}>
              {editingAddr === addr.address_id ? (
                <AddressForm addrForm={addrForm} setAddrForm={setAddrForm} addrLoading={addrLoading} handleSaveAddress={handleSaveAddress} onCancel={() => setEditingAddr(null)} />
              ) : (
                <AddressDisplay addr={addr} onEdit={() => { setEditingAddr(addr.address_id); setAddrForm({ label: addr.label, address: addr.address, pincode: addr.pincode || '' }); }} onDelete={() => handleDeleteAddress(addr.address_id)} onSetDefault={() => handleSetDefault(addr.address_id)} />
              )}
            </div>
          ))}
        </div>
      ) : editingAddr !== 'new' ? (
        <div className="text-center py-4 text-[#475569]">
          <MapPin size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-xs">No saved addresses yet. Add one for faster bookings!</p>
        </div>
      ) : null}

      {editingAddr === 'new' && (
        <div className="mt-3 p-3 rounded-xl border border-dashed border-[#E83E8C]/30 bg-[#E83E8C]/5 space-y-2">
          <AddressForm addrForm={addrForm} setAddrForm={setAddrForm} addrLoading={addrLoading} handleSaveAddress={handleSaveAddress} onCancel={() => setEditingAddr(null)} saveLabel="Save Address" />
        </div>
      )}
    </div>
  );
};

const AddressForm = ({ addrForm, setAddrForm, addrLoading, handleSaveAddress, onCancel, saveLabel = "Save" }) => (
  <div className="space-y-2">
    <div className="flex gap-2">
      {['Home', 'Office', 'Other'].map(lbl => (
        <button key={lbl} type="button" onClick={() => setAddrForm(p => ({ ...p, label: lbl }))}
          className={`px-3 py-1 text-xs rounded-full border transition-all ${addrForm.label === lbl ? 'bg-[#E83E8C] text-white border-[#E83E8C]' : 'border-gray-200 text-[#475569] hover:border-[#E83E8C]/40 bg-white'}`}>
          {lbl}
        </button>
      ))}
    </div>
    <textarea value={addrForm.address} onChange={e => setAddrForm(p => ({ ...p, address: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-[#E83E8C] outline-none resize-none" placeholder="Full address" />
    <input value={addrForm.pincode} onChange={e => setAddrForm(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-[#E83E8C] outline-none" placeholder="Pincode" maxLength={6} />
    <div className="flex gap-2">
      <button onClick={handleSaveAddress} disabled={addrLoading || !addrForm.address.trim()} className="px-4 py-1.5 text-xs rounded-lg bg-[#E83E8C] text-white font-medium disabled:opacity-50">{addrLoading ? 'Saving...' : saveLabel}</button>
      <button onClick={onCancel} className="px-4 py-1.5 text-xs rounded-lg border border-gray-200 text-[#475569]">Cancel</button>
    </div>
  </div>
);

const AddressDisplay = ({ addr, onEdit, onDelete, onSetDefault }) => (
  <div className="flex items-start justify-between gap-2">
    <div className="flex-1">
      <div className="flex items-center gap-1.5 mb-0.5">
        {addr.label?.toLowerCase().includes('office') || addr.label?.toLowerCase().includes('work') ? <Building size={12} className="text-[#475569]" /> : <Home size={12} className="text-[#475569]" />}
        <span className="text-xs font-semibold text-[#1E1B4B] uppercase">{addr.label}</span>
        {addr.is_default && <span className="text-[9px] px-1.5 py-0.5 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full font-medium">Default</span>}
      </div>
      <p className="text-xs text-[#475569] leading-relaxed">{addr.address}</p>
      {addr.pincode && <p className="text-[10px] text-[#94a3b8] mt-0.5">PIN: {addr.pincode}</p>}
      {hasPin(addr) && (
        <a
          href={mapsUrlFor(addr.location_lat, addr.location_lng)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-medium border border-emerald-200 hover:bg-emerald-100 transition-colors"
          title="Location pin saved — tap to open in Maps"
          data-testid={`address-map-pin-${addr.address_id}`}
        >
          <Navigation size={10} /> Pinned · View on map
        </a>
      )}
    </div>
    <div className="flex items-center gap-1 shrink-0">
      {!addr.is_default && (
        <button onClick={onSetDefault} className="p-1.5 rounded-lg hover:bg-[#E83E8C]/10 transition-colors" title="Set as default" data-testid={`set-default-${addr.address_id}`}>
          <Star size={13} className="text-[#94a3b8]" />
        </button>
      )}
      <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-indigo-50 transition-colors" title="Edit" data-testid={`edit-address-${addr.address_id}`}>
        <Edit2 size={13} className="text-indigo-500" />
      </button>
      <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Delete" data-testid={`delete-address-${addr.address_id}`}>
        <Trash2 size={13} className="text-red-400" />
      </button>
    </div>
  </div>
);
